import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

export default function Credits() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [accounts, setAccounts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", date: todayISO() });

  const load = () => api.get("/credits").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, []);

  const totals = {
    total: rows.reduce((s, c) => s + (c.remaining || 0), 0),
    late: rows.filter((c) => c.status === "En retard").length,
    active: rows.filter((c) => !["Entièrement payé"].includes(c.status)).length,
  };

  const filtered = rows.filter((c) => {
    if (tab === "open") return !["Entièrement payé"].includes(c.status);
    if (tab === "late") return c.status === "En retard";
    if (tab === "done") return c.status === "Entièrement payé";
    return true;
  });

  const pay = async () => {
    try {
      await api.post(`/credits/${payTarget.credit.id}/installments/${payTarget.inst.n}/pay`, {
        amount: Number(payForm.amount), account_id: payForm.account_id || null, date: payForm.date,
      });
      toast.success("Échéance encaissée");
      setPayTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="credits-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Crédits clients</h1>
        <p className="mt-1 text-sm text-slate-500">
          Encours total : <span className="font-mono-num font-bold text-amber-600">{fmtMoney(totals.total)}</span> · {totals.active} crédit(s) actif(s) · <span className="font-semibold text-red-600">{totals.late} en retard</span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["all", "Tous"], ["open", "En cours"], ["late", "Impayés / retard"], ["done", "Soldés"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"}
            className={tab === k ? "bg-cyan-600 hover:bg-cyan-700" : ""}
            onClick={() => setTab(k)} data-testid={`credits-tab-${k}`}>{l}</Button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="rounded-xl border border-slate-200 bg-white py-12 text-center text-slate-400" data-testid="credits-empty">Aucun crédit client.</div>}
        {filtered.map((c) => {
          const pct = c.total ? Math.round((c.paid / c.total) * 100) : 0;
          return (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`credit-card-${c.sale_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-800">{c.customer_name} <span className="ml-1 font-mono-num text-xs text-slate-400">{c.sale_number}</span></p>
                  <p className="text-sm text-slate-500">{c.vehicle_label}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono-num text-sm"><span className="text-emerald-600 font-semibold">{fmtMoney(c.paid)}</span> / {fmtMoney(c.total)}</p>
                    <p className="font-mono-num text-sm font-bold text-red-600">Reste {fmtMoney(c.remaining)}</p>
                  </div>
                  <StatusBadge status={c.status} />
                  <Button variant="ghost" size="icon" onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`expand-credit-${c.sale_number}`}>
                    {expanded === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              {expanded === c.id && (
                <div className="mt-4 overflow-x-auto rounded-lg border border-slate-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2">N°</th><th className="px-3 py-2">Échéance</th><th className="px-3 py-2">Montant</th>
                        <th className="px-3 py-2">Payé</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.installments.map((i) => {
                        const overdue = i.status !== "Payée" && new Date(i.due_date) < new Date(todayISO());
                        return (
                          <tr key={i.n} className={`border-b border-slate-50 last:border-0 ${overdue ? "bg-red-50/50" : ""}`} data-testid={`installment-row-${c.sale_number}-${i.n}`}>
                            <td className="px-3 py-2">{i.n}</td>
                            <td className="px-3 py-2">{fmtDate(i.due_date)}{overdue && <span className="ml-2 text-xs font-semibold text-red-600">EN RETARD</span>}</td>
                            <td className="px-3 py-2 font-mono-num">{fmtMoney(i.amount)}</td>
                            <td className="px-3 py-2 font-mono-num">{fmtMoney(i.paid_amount)}</td>
                            <td className="px-3 py-2"><StatusBadge status={i.display_status || i.status} /></td>
                            <td className="px-3 py-2 text-right">
                              {i.status !== "Payée" && (
                                <Button size="sm" variant="outline" data-testid={`pay-installment-${c.sale_number}-${i.n}`}
                                  onClick={() => { setPayTarget({ credit: c, inst: i }); setPayForm({ amount: String(i.amount - (i.paid_amount || 0)), account_id: "", date: todayISO() }); }}>
                                  Encaisser
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Encaisser l'échéance n°{payTarget?.inst.n} — {payTarget?.credit.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Montant (FCFA)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="installment-pay-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
            <div><Label>Compte crédité</Label>
              <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="installment-pay-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={pay} data-testid="installment-pay-submit">Valider l'encaissement</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
